import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useOrg } from '../../contexts/OrgContext'
import { useAIJobs } from '../../contexts/AIJobsContext'
import { useInsightsData } from '../../features/insights/hooks/useInsightsData'
import { useDemoPlan } from '../../hooks/useDemoPlan'
import { useAssessment } from '../../contexts/AssessmentContext'
import { buildAdvisoryReportContext } from '../utils/advisoryReportAggregator'
import { exportAdvisoryReport } from '../utils/advisoryReportExport'
import { getTopPriorities } from '../utils/scoring'

// Single source of truth for the "Generate Advisory Report" action.
// Usage:
//   <AdvisoryReportButton assessmentId={id} client={client} />
//
// When assessmentId is null/in-progress, the button is disabled with a tooltip.
function AdvisoryReportButton({ assessmentId, client, variant = 'primary', size = 'sm' }) {
  const { currentOrg } = useOrg()
  const { startJob, getJob } = useAIJobs()
  const { isDemoOrg, demoMessage } = useDemoPlan()
  const { getAssessment } = useAssessment()
  const { raw } = useInsightsData(assessmentId)

  const [orgBrandConfig, setOrgBrandConfig] = useState(null)
  const [clientEnrichment, setClientEnrichment] = useState(null)
  const [error, setError] = useState(null)
  const [demoNotice, setDemoNotice] = useState('')

  const jobKey = assessmentId ? `advisory-report:${assessmentId}` : null
  const job = jobKey ? getJob(jobKey) : null
  const running = job?.status === 'running'

  useEffect(() => {
    if (!currentOrg?.id) return
    supabase
      .from('org_brand_config')
      .select('*')
      .eq('org_id', currentOrg.id)
      .maybeSingle()
      .then(({ data }) => { if (data) setOrgBrandConfig(data) })
  }, [currentOrg?.id])

  useEffect(() => {
    if (!client?.id) return
    supabase
      .from('client_enrichment')
      .select('*')
      .eq('client_id', client.id)
      .maybeSingle()
      .then(({ data }) => { if (data) setClientEnrichment(data) })
  }, [client?.id])

  const canGenerate = Boolean(assessmentId && raw?.scores && client)
  const disabledReason = !assessmentId
    ? 'No completed assessment available'
    : !raw?.scores
    ? 'Scores not available for this assessment'
    : null

  const handleClick = async () => {
    if (isDemoOrg) {
      setDemoNotice(demoMessage('Advisory Report exports'))
      setTimeout(() => setDemoNotice(''), 8000)
      return
    }
    if (!canGenerate) return
    setError(null)

    const { assessment } = getAssessment(assessmentId) || {}
    const priorities = raw?.tapsResults ? getTopPriorities(raw.scores, raw.alignments, raw.benchmarkData) : []
    const applicableFrameworks =
      raw?.benchmarkData?.industryInfo?.keyFrameworks ||
      clientEnrichment?.enrichment?.regulatoryProfile?.applicableFrameworks ||
      []

    const context = buildAdvisoryReportContext({
      client,
      assessment,
      scores: raw.scores,
      benchmarkData: raw.benchmarkData,
      alignments: raw.alignments,
      priorities,
      clientEnrichment,
      applicableFrameworks
    })

    try {
      const { data: { session } } = await supabase.auth.getSession()

      const result = await startJob(jobKey, async () => {
        const res = await fetch('/api/ai/advisory-report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
          },
          body: JSON.stringify({ section: 'executive-summary', context })
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.error || 'Advisory report generation failed')
        }
        return await res.json()
      })

      await exportAdvisoryReport({
        client,
        context,
        draft: result?.draft,
        review: result?.review,
        brand: {
          firmName: orgBrandConfig?.firm_display_name || currentOrg?.name || 'Sidewalk Security Advisors',
          logoUrl: orgBrandConfig?.logo_url || null,
          primaryHex: orgBrandConfig?.primary_color || '0D1B2A',
          accentHex: orgBrandConfig?.accent_color || 'C9A84C',
          trademarkLine: orgBrandConfig?.trademark_line || null
        }
      })
    } catch (err) {
      console.error('[AdvisoryReport] Generation failed:', err)
      setError(err?.message || 'Advisory report failed')
    }
  }

  const baseClasses = 'inline-flex items-center gap-1.5 rounded-lg transition-colors shadow-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed'
  const sizeClasses = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
  const variantClasses = variant === 'primary'
    ? 'text-white bg-primary-600 hover:bg-primary-700'
    : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'

  return (
    <div className="inline-flex flex-col items-end">
      <button
        type="button"
        onClick={handleClick}
        disabled={running || !canGenerate}
        className={`${baseClasses} ${sizeClasses} ${variantClasses}`}
        title={disabledReason || 'Generate AI-drafted executive advisory report (POC: Executive Summary only)'}
      >
        {running ? (
          <>
            <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating...
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Advisory Report
          </>
        )}
      </button>
      {error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400 max-w-xs text-right">{error}</p>
      )}
      {demoNotice && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 max-w-xs text-right">{demoNotice}</p>
      )}
    </div>
  )
}

export default AdvisoryReportButton
